// stores/mainStore.js
import { defineStore } from "pinia";
import {
  getWebsiteHomePageInfo,
  getTabBarConfigList,
  getWebStorePageInfo,
  getActiveDetail,
} from "~/api";
import { getCurrentUserData, getCurrentMallUserData } from "~/api/mall";

import { get, set } from "lodash-es";

import {
  mockJwtMap,
  getPrefixUrl,
  getCurrentServer,
  getRequestSiteHost,
} from "@/utils/globalData";
import { logStoreFetchError } from "@/utils/requestDebug";
import { localizeShopProfile } from "../utils/i18nJson.js";

import config from "@/config/index";
import { querySassmallDealerShop, getSassmallActivePage } from "@/sassmall/api/site";
import { querySassmallCurrentUser } from "@/sassmall/api/auth";
import { getSassmallWalletAccount } from "@/sassmall/api/account";
import { listSassmallTenantExchangeRates } from "@/sassmall/api/system";
import { clearSassmallAuth, getSassmallTenantDomain, getSassmallToken } from "@/sassmall/storage";
import { isSassmallResponseSuccess } from "@/sassmall/api/client";
import { applyAccountCurrencyPreference } from "@/sassmall/currencyPreference";
// 创建 Pinia store
export const useMainStore = defineStore("main", {
  state: () => ({
    token: "",
    userInfo: {},
    mallUserInfo: {},
    sassmallUserInfo: {},
    sassmallWalletAccount: {},
    sassmallTenantDomain: "",
    sassmallReady: false,
    tokenKeyMap: {},
    storeInfoDataMap: {},
    storeInfoSourceDataMap: {},
    shopLocale: "",
    activeStoreServer: "",
    currencyList: [],
    accountCurrencyApplied: false,
    localeCurrencyReady: false,
    localeCurrencyError: false,
    currentCurrency: {},
    layoutData: {},
    menuData: [],
    menuIdMap: {},
    navigationList: [],
    siteInfo: {},
  }),

  actions: {
    // 设置 Token
    setToken(token) {
      this.token = token;
    },

    // 设置用户信息
    setUserInfo(userInfo) {
      this.userInfo = userInfo;
    },
    setSassmallUser(userInfo = {}, walletAccount = {}) {
      this.sassmallUserInfo = userInfo;
      this.sassmallWalletAccount = walletAccount;
      this.sassmallReady = Boolean(getSassmallToken());
    },
    clearSassmallUser() {
      clearSassmallAuth();
      this.sassmallUserInfo = {};
      this.sassmallWalletAccount = {};
      this.sassmallReady = false;
    },
    async bootstrapSassmallSession() {
      if (!process.client || !getSassmallToken()) {
        this.sassmallReady = false;
        return null;
      }
      try {
        const userResponse = await querySassmallCurrentUser();
        if (!isSassmallResponseSuccess(userResponse) || !userResponse.data) {
          throw new Error(userResponse.message || userResponse.msg || "登录状态已失效");
        }
        let walletAccount = {};
        try {
          const walletResponse = await getSassmallWalletAccount();
          if (isSassmallResponseSuccess(walletResponse)) walletAccount = walletResponse.data || {};
        }
        catch (error) {
          console.warn("sassmall wallet bootstrap failed", error);
        }
        this.setSassmallUser(userResponse.data, walletAccount);
        return userResponse.data;
      }
      catch (error) {
        this.clearSassmallUser();
        throw error;
      }
    },

    // 设置菜单数据
    setMenuData(menuData) {
      this.menuData = menuData;
    },

    // 设置菜单 ID Map
    setMenuIdMap(menuIdMap) {
      this.menuIdMap = menuIdMap;
    },
    // 设置 不同店铺的map
    setStoreInfoDataMap(key, val) {
      this.storeInfoSourceDataMap[key] = val;
      this.activeStoreServer ||= key;
      this.storeInfoDataMap[key] = localizeShopProfile(
        val,
        this.shopLocale || val?.defaultLanguage,
      );
      this.syncShopSiteInfo();
    },
    applyShopLocale(locale) {
      this.shopLocale = this.normalizeMallSiteLocale(locale);
      for (const [key, source] of Object.entries(this.storeInfoSourceDataMap)) {
        this.storeInfoDataMap[key] = localizeShopProfile(source, this.shopLocale);
      }
      this.syncShopSiteInfo();
    },
    syncShopSiteInfo() {
      const shop = this.storeInfoDataMap[this.activeStoreServer];
      if (!shop) return;
      this.setSiteInfo({
        ...this.siteInfo,
        pc_SITE_NAME: shop.title || "",
        mobile_SITE_NAME: shop.title || "",
        logo: shop.logoUrl || "",
        introduction: shop.introduction || "",
        companyName: shop.companyName || "",
        maxim: shop.maxim || "",
      });
      if (process.client) {
        try {
          window.localStorage.setItem("StoreInformation", JSON.stringify(shop));
        } catch (error) {
          console.log(error);
        }
      }
    },
    // 获取 不同店铺的值
    getStoreInfoDataMap(key) {
      return this.storeInfoDataMap[key];
    },
    // 设置 Token Key Map
    setTokenKeyMap(key, val) {
      this.tokenKeyMap[key] = val;
    },

    // 设置站点信息
    setSiteInfo(data) {
      this.siteInfo = data;
    },
    getSiteInfo() {
      return this.siteInfo;
    },
    getMallSiteConfig(currentServer = getCurrentServer()) {
      const storeInfo = this.storeInfoDataMap[currentServer] || {};
      return (
        get(storeInfo, "storePageInfo.mallSiteConfig") ||
        get(storeInfo, "storePageInfo.otherJson.mallSiteConfig") ||
        get(storeInfo, "mallSiteConfig") ||
        {}
      );
    },
    getMallPageConfig(currentServer = getCurrentServer()) {
      const storeInfo = this.storeInfoDataMap[currentServer] || {};
      
      return get(storeInfo, "storePageInfo.mallPageConfig", []);
    },
    getMatchedMallPageConfig(pageKey, currentServer = getCurrentServer()) {
      const mallPageConfig = this.getMallPageConfig(currentServer);
      const pageConfigList = Array.isArray(mallPageConfig) ? mallPageConfig : [];
      const matchedConfigList = pageConfigList.filter((item) => item?.pageKey == pageKey);

      return matchedConfigList.find((item) => {
        const matchList = Array.isArray(item?.match) ? item.match : [];
        return matchList.some((matchUrl) => {
          return String(matchUrl || "").indexOf(String(currentServer || "")) >= 0;
        });
      }) || null;
    },
    getCommonLayoutConfig(currentServer = getCurrentServer()) {
      const headerMatchConfig = this.getMatchedMallPageConfig("layout-header", currentServer);
      const footerMatchConfig = this.getMatchedMallPageConfig("layout-footer", currentServer);

      return {
        header: {
          ...config.layoutCommonConfig.header,
          // mallPageConfig 命中当前域名时，允许同店铺不同域名切不同 header。
          url: headerMatchConfig?.pageUrl || config.layoutCommonConfig.header.url,
        },
        footer: {
          ...config.layoutCommonConfig.footer,
          // mallPageConfig 命中当前域名时，允许同店铺不同域名切不同 footer。
          url: footerMatchConfig?.pageUrl || config.layoutCommonConfig.footer.url,
        },
      };
    },
    normalizeMallSiteLocale(localeCode) {
      const locale = String(localeCode || "").trim();
      if (!locale) {
        return "";
      }

      const normalizedKey = locale.replace(/_/g, "-").toLowerCase();
      const localeMap = {
        zh: "zh-CN",
        "zh-cn": "zh-CN",
        "zh-hans": "zh-CN",
        "zh-hans-cn": "zh-CN",
        "zh-tw": "zh-TW",
        "zh-hk": "zh-TW",
        "zh-hant": "zh-TW",
        "zh-hant-tw": "zh-TW",
        en: "en",
        "en-us": "en",
        "en-gb": "en",
        vi: "vi_VN",
        "vi-vn": "vi_VN",
        ja: "ja_JP",
        "ja-jp": "ja_JP",
      };

      return localeMap[normalizedKey] || locale;
    },
    getMallSiteDefaultLocale(currentServer = getCurrentServer()) {
      const supportedLocales = ["zh-CN", "zh-TW", "vi_VN", "en", "ja_JP"];
      const candidates = [
        this.storeInfoDataMap[currentServer]?.defaultLanguage,
        get(this.getMallSiteConfig(currentServer), "defaultLocale"),
      ];
      for (const candidate of candidates) {
        const locale = this.normalizeMallSiteLocale(candidate);
        if (supportedLocales.includes(locale)) return locale;
      }
      return "";
    },
    getMallSiteDefaultCurrency(currentServer = getCurrentServer()) {
      return String(
        this.storeInfoSourceDataMap[currentServer]?.defaultCurrencyCode ||
        get(this.getMallSiteConfig(currentServer), "defaultCurrency") || ""
      )
        .trim()
        .toUpperCase();
    },

    // 深度设置菜单 ID Map
    deepSetIdMap(menuData, typeMap) {
      menuData.forEach((o) => {
        if (o.id) {
          typeMap[`id_${o.id}`] = o;
        }

        if (o.noticeColumnEntityList && o.noticeColumnEntityList.length) {
          this.deepSetIdMap(o.noticeColumnEntityList, typeMap);
        }
      });
    },

    // 获取用户信息
    async fetchUserInfo() { },

    // 获取 Token
    getTokenByKey(key, headers) {
      return this.tokenKeyMap[key] || mockJwtMap[getPrefixUrl("", headers)];
    },
    // 获取站点菜单
    async fetchSiteMenu($fetch, headers) {
      const currentServer = getCurrentServer(headers);
      const param = {
        pageNo: 0,
        pageSize: 9999,
        showType: "1",
        status: "1",
      };
      try {
        const res = await getTabBarConfigList(param, $fetch, headers);
        if (res.data && res.data.dataList) {
          const list = res.data.dataList.sort((a, b) => {
            const aSort = a.sortNo ?? 0;
            const bSort = b.sortNo ?? 0;
            return aSort - bSort;
          });
          this.navigationList = list;
        }
      } catch (e) {
        logStoreFetchError("fetchSiteMenu", headers, e, { currentServer });
      }

      return this.navigationList;
    },

    // 获取网站店铺信息
    async fetchStoreInfo(currentServer, $fetch, headers) {
      let storeRes = {};
      this.activeStoreServer = currentServer;

      try {
        // Sassmall bootstrap must use the address that opened this Nuxt app.
        // getCurrentServer() intentionally replaces bare localhost with the
        // legacy mall mock domain, which is not a valid Sassmall tenant.
        storeRes = await querySassmallDealerShop({
          url: getRequestSiteHost(headers) || currentServer,
        });
      } catch (e) {
        logStoreFetchError("fetchStoreInfo", headers, e, { currentServer });
      }

      if (storeRes?.data) {
        const sassmallOtherJson = get(storeRes, "data.otherJson", {});
        if (sassmallOtherJson && typeof sassmallOtherJson === "object") {
          set(storeRes, "data.storePageInfo", {
            otherJson: sassmallOtherJson,
            mallSiteConfig: get(sassmallOtherJson, "mallSiteConfig", {}),
            mallPageConfig: get(sassmallOtherJson, "mallPageConfig", []),
          });
        }
        try {
          if (typeof get(storeRes, "data.offiaccountFollowUrl") == "string") {
            set(
              storeRes,
              "data.storePageInfo",
              JSON.parse(get(storeRes, "data.offiaccountFollowUrl"))
            );
          }
        } catch (e) {
          console.log(e);
        }
        try {
          if (
            typeof get(storeRes, "data.storePageInfo.otherJson") == "string"
          ) {
            set(
              storeRes,
              "data.storePageInfo",
              JSON.parse(get(storeRes, "data.storePageInfo.otherJson"))
            );
          }
        } catch (e) {
          console.log(e);
        }

        this.setStoreInfoDataMap(currentServer, storeRes.data);
        this.sassmallTenantDomain = storeRes.__tenantDomain
          || storeRes["x-tenant-domain"]
          || getSassmallTenantDomain()
          || currentServer;
        const defaultCurrency = String(
          storeRes.data.defaultCurrencyCode
          || get(sassmallOtherJson, "mallSiteConfig.defaultCurrency")
          || "",
        ).toUpperCase();
        if (process.client) {
          this.initCurrentCurrency();
        }
        if (!get(this.currentCurrency, "toCurrencyCode") && defaultCurrency) {
          this.currentCurrency = {
            id: `default-${defaultCurrency}`,
            fromCurrencyCode: defaultCurrency,
            toCurrencyCode: defaultCurrency,
            exchangeRate: 1,
            activeFlag: true,
            deleteFlag: false,
          };
        }
      }
    },



    // 获取站点信息
    async fetchSiteInfo(currentServer, $fetch, headers) {
      let siteKeyRes = {};

      try {
        siteKeyRes = await getWebsiteHomePageInfo({ currentServer }, $fetch, headers);
      } catch (e) {
        logStoreFetchError("fetchSiteInfo", headers, e, { currentServer });
      }

      if (siteKeyRes?.data) {
        this.setSiteInfo(siteKeyRes.data);
      }
    },

    async fetchLayoutConfig($fetch, headers) {
      let layoutData = {};
      const currentServer = getCurrentServer(headers);
      const commonLayoutConfig = this.getCommonLayoutConfig(currentServer);
      let commonLayouts = [commonLayoutConfig.header.url, commonLayoutConfig.footer.url];
      let currentLayoutUrl = "";
      try {
        for (let i = 0; i < commonLayouts.length; i++) {
          currentLayoutUrl = commonLayouts[i];
          let layoutConfigItemResponse = await getSassmallActivePage({
            url: commonLayouts[i],
            pageNo: 1,
            pageSize: 1,
          }, this.sassmallTenantDomain);
          if (get(layoutConfigItemResponse, "data.dataList[0]")) {
            let areaData = get(
              layoutConfigItemResponse,
              "data.dataList[0].areaData"
            );
            let areaDataParseData = null;
            try {
              areaDataParseData = JSON.parse(areaData);
            } catch (e) {
              console.log(e);
            }
            if (areaDataParseData) {
              layoutData[commonLayouts[i]] = areaDataParseData;
            }
          }
        }

        if (process.env.NUXT_BOOTSTRAP_DEBUG === "1") {
          console.info(JSON.stringify({
            type: "nuxt:layout-config-loaded",
            currentServer,
            layoutKeys: Object.keys(layoutData),
          }));
        }
      } catch (e) {
        logStoreFetchError("fetchLayoutConfig", headers, e, {
          currentServer,
          layoutUrl: currentLayoutUrl,
        });
      }
      this.layoutData = layoutData;
    },
    async getCurrentUserData() {
      let appToken = window.localStorage.getItem(config.appTokenKey);
      let mallAppToken = window.localStorage.getItem(config.mallAppTokenKey);
      let currentUserData = {};
      let currentMallUserData = {};
      if (appToken) {
        let currentUserDataRes = await getCurrentUserData({});

        currentUserData = get(currentUserDataRes, "data.dataList[0]", {});
      }
      if (mallAppToken && currentUserData.memberCode) {
        let currentMallUserDataRes = await getCurrentMallUserData({
          param: {
            thirdParty: 5,
            thirdPartyType: "SZT",
            thirdPartyId: currentUserData.memberCode,
          },
        });

        currentMallUserData = get(currentMallUserDataRes, "data", {});
      }
      if (!mallAppToken && currentUserData.memberCode) {
        try {
          const currentMallUserDataRes = await getCurrentMallUserData({
            param: {
              thirdParty: 5,
              thirdPartyType: "SZT",
              thirdPartyId: currentUserData.memberCode,
            },
          });

          currentMallUserData = get(currentMallUserDataRes, "data", currentMallUserData);
        } catch (error) {
          console.log("getCurrentMallUserData fallback error", error);
        }
      }

      this.userInfo = currentUserData;
      this.mallUserInfo = currentMallUserData;

      if (
        !get(this.currentCurrency, "toCurrencyCode") &&
        this.getMallSiteDefaultCurrency()
      ) {
        await this.getCurrencyData();
      }
    },
    saveCurrentCurrency(toCurrency) {
      try {
        if (toCurrency) {


          window.localStorage.setItem('currentCurrency', JSON.stringify(toCurrency));

          this.currentCurrency = toCurrency;
        }
      } catch (e) {
        console.log(e)
      }
    },
    initCurrentCurrency() {
      if (!process.client) return null;
      try {
        const currentCurrencyStr = window.localStorage.getItem('currentCurrency');
        if (!currentCurrencyStr) return null;
        const currentCurrency = JSON.parse(currentCurrencyStr);
        if (!currentCurrency || !get(currentCurrency, "toCurrencyCode")) return null;
        this.currentCurrency = currentCurrency;
        return currentCurrency;
      } catch (e) {
        console.log(e)
        return null;
      }
    },
    async getCurrencyData() {
      if (!process.client) return;
      const savedCurrencySource = window.localStorage.getItem('currentCurrencySource');
      const savedCurrency = this.initCurrentCurrency();
      const savedCurrencyCode = String(savedCurrency?.toCurrencyCode || '').toUpperCase();
      const legacyFallbackCurrency = !savedCurrencySource && savedCurrencyCode === 'CNY' &&
        savedCurrency?.id === 'CNY';
      const hadLocalCurrency = Boolean(savedCurrencyCode) && savedCurrencySource !== 'AUTO' &&
        !legacyFallbackCurrency;
      const currentServer = this.activeStoreServer || getCurrentServer();
      if (!this.storeInfoDataMap[currentServer]) {
        throw new Error('店铺信息尚未加载');
      }
      if (this.storeInfoDataMap[currentServer]) {
        const sassmallCurrencyList = await listSassmallTenantExchangeRates("CNY");
        const currencyList = sassmallCurrencyList.map((item) => ({
          id: String(item.id || `${item.fromCurrency}-${item.toCurrency}`),
          fromCurrencyCode: String(item.fromCurrency || "").toUpperCase(),
          toCurrencyCode: String(item.toCurrency || "").toUpperCase(),
          exchangeRate: Number(item.exchangeRate),
          activeFlag: String(item.status || "ACTIVE").toUpperCase() === "ACTIVE",
          deleteFlag: false,
        })).filter((item) => (
          item.activeFlag
          && item.fromCurrencyCode
          && item.toCurrencyCode
          && Number.isFinite(item.exchangeRate)
          && item.exchangeRate > 0
        ));
        const nextCurrencyList = [
          {

            id: "1",
            fromCurrencyCode: "CNY",
            toCurrencyCode: "CNY",
            exchangeRate: 1,
            activeFlag: true,
            deleteFlag: false,

          },
          ...currencyList
        ];
        const seenCurrencyMap = new Set();
        this.currencyList = nextCurrencyList.filter((item) => {
          const toCurrencyCode = String(item?.toCurrencyCode || "").trim().toUpperCase();
          const exchangeRate = String(item?.exchangeRate ?? "").trim();
          const currencyKey = toCurrencyCode;
          if (!toCurrencyCode || seenCurrencyMap.has(currencyKey)) {
            return false;
          }
          seenCurrencyMap.add(currencyKey);
          return true;
        });
        const selectedCurrencyCode = String(
          get(this.currentCurrency, "toCurrencyCode") || ""
        ).trim().toUpperCase();
        const defaultCurrencyCode = this.getMallSiteDefaultCurrency(currentServer);
        const selectedCurrencyItem = this.currencyList.find((item) => (
          String(item?.toCurrencyCode || "").trim().toUpperCase() === selectedCurrencyCode
        ));
        const defaultCurrencyItem = this.currencyList.find((item) => (
          String(item?.toCurrencyCode || "").trim().toUpperCase() === defaultCurrencyCode
        ));
        const nextCurrencyItem = selectedCurrencyItem || defaultCurrencyItem || this.currencyList[0];

        if (nextCurrencyItem) {
          this.saveCurrentCurrency(nextCurrencyItem);
        }
        try {
          this.accountCurrencyApplied = await applyAccountCurrencyPreference(this, hadLocalCurrency);
        } catch (error) {
          console.warn("账号币种偏好加载失败:", error);
        }
      }

    },

    async logout() {
      this.userInfo = {};
      this.mallUserInfo = {};
      this.clearSassmallUser();
      removeToken()
      removeMallAppToken();
      if (process.client) {
        window.location.reload();
      }
    }
  },
});
